/* eslint-disable brace-style */
import {
    call,
    put,
    select,
    takeLatest,
} from 'redux-saga/effects';
import {
    RequestParams,
    requestHtml,
} from '@vfde-sails/utils';
import {
    AppAction,
    ErrorType,
} from './enum';
import {
    trackPageViewSaga,
    getInitialTariffs,
    handleBannerId,
    handleLogin,
    setPageUrl,
    validateFriendsAndFamily,
    handleStepper,
} from './util/generator';
import {
    setError,
    setGigakombi,
    setTVProductSelection,
    Actions,
    setBannerId,
    setYoung,
    startLoading,
    stopLoading,
} from './action';
import {
    selectBannerId,
    selectGigakombi,
    selectSelectedTVProduct,
    selectSelectedTariff,
} from './selector';
import {
    JarvisProduct,
    JarvisTvProduct,
} from '../../interface';
import { getProductPageUrl } from '../../util/url';
import {
    deleteAddressCookie,
    getHandshake,
} from '@vfde-sails/avc-core';
import { createFootnoteAccordionItem } from '../../components/accordion';
import { debugInfo } from '../../util/debug';
import { getTvFootnoteUrl } from '../../util/cms';
import { createFootnoteContent } from '../../util/formatter';
import {
    updateLegalFootnoteText,
    updateTvPickerText,
} from '../../components/justText';
import { checkSeamlessAvailability } from '../AvailabilityCheck/action';
import { mountFootnoteLink } from '../../components/footnoteLink';
import {
    DiscountId,
    LegalId,
} from '../../enums/elementId';
import {
    trackError,
    trackGigakombiSwitcher,
    trackTvSwitcher,
} from '../../tracking/tracking';
import { SCROLL_POSITION_KEY } from '../../constants';
import { setSessionStorageItemJson } from '@vfde-sails/storage';
import { deleteAddressState } from '../../util/jarvis';
import { removeGigakombiCookie } from '../../util/cookie';

/**
 * Loads young/default page based on young selection
 */
export function* setYoungSaga ({ isYoung }: ReturnType<typeof setYoung>) {
    try {
        const selectedTariff: JarvisProduct | undefined = yield select(selectSelectedTariff);

        if (!selectedTariff) {
            throw new Error('Jarvis product not found.');
        }

        const { id, productType } = selectedTariff;
        const isGigakombi: boolean = yield select(selectGigakombi);

        const url: string = yield call(getProductPageUrl, id, productType, isGigakombi, isYoung);

        yield call(setSessionStorageItemJson, SCROLL_POSITION_KEY, window.scrollY);
        location.assign(url);
    } catch (error) {
        yield put(setError({
            type: ErrorType.JARVIS,
            error: error as Error,
        }));
        yield call(trackError, error as Error, ErrorType.JARVIS);
    }
}

/**
 * Updates page url after tariff selection
 */
export function* setSelectedTariffSaga () {
    yield call(setPageUrl);
    yield call(trackPageViewSaga);
}

/**
 * Updates page url after tariff selection
 */
export function* setGigakombiSaga ({ isGigakombi }: ReturnType<typeof setGigakombi>) {
    const selectedTariff: JarvisProduct | undefined = yield select(selectSelectedTariff);

    if (selectedTariff) {
        yield call(trackGigakombiSwitcher, selectedTariff, isGigakombi);
    }

    yield call(setPageUrl);
}

/**
 * Tracks the TV option picker
 */
export function* setTVSaga ({ isTVProductSelected }: ReturnType<typeof setTVProductSelection>) {
    const selectedTVProduct: JarvisTvProduct | undefined = yield select(selectSelectedTVProduct);

    if (selectedTVProduct) {
        yield call(trackTvSwitcher, selectedTVProduct, isTVProductSelected);
    }
}

/**
 * Triggers F&F validation, initial tariff fetch, and b_id check
 * Inits seamless check after all necessary information loaded
 * Force seamless check if banner id changed
 */
export function* initStoreSaga (action: Actions) {
    // Remove gigakombi cookie to prevent stucked discount
    yield call(removeGigakombiCookie);
    yield call(handleStepper);
    yield call(handleLogin, action);
    yield call(validateFriendsAndFamily, action);
    yield call(getInitialTariffs, action);

    const bannerId: string | undefined = yield call(handleBannerId, action);
    const prevBannerId: string | undefined = yield select(selectBannerId);

    yield put(checkSeamlessAvailability(bannerId !== prevBannerId));

    yield put(setBannerId(bannerId));
}

/**
 * Deletes address from cookie
 */
export function* deleteAddressSaga (action: Actions) {
    yield call(deleteAddressCookie);

    try {
        yield put(startLoading(action.type));
        yield call(setPageUrl);
        const gimliState: string | undefined = yield call(getHandshake);

        if (gimliState) {
            yield call(deleteAddressState, gimliState);
        }
    } catch (error) {
        yield call(debugInfo, 'Delete state address failed:', error);
    } finally {
        yield put(stopLoading(action.type));
    }
}

/**
 * Fetches the HTML for the available TV Connect's footnote
 */
export function* setFootnoteContentSaga () {
    const tvTariff: JarvisTvProduct | undefined = yield select(selectSelectedTVProduct);

    if (!tvTariff) {
        // Note: In theory, this will never happen, because we check this in index.ts#L141
        return;
    }

    try {
        yield put(startLoading(AppAction.LOAD_TV_FOOTNOTE));

        const options: RequestParams = {};



        const footnoteHTML: string = yield call(requestHtml, getTvFootnoteUrl(tvTariff.tvGroup), options);
        const footnoteContent = createFootnoteContent(footnoteHTML, tvTariff);

        // Note: Update the corresponding components with the fetched data
        if (footnoteContent) {
            createFootnoteAccordionItem(footnoteContent);
            updateLegalFootnoteText(footnoteContent);
            updateTvPickerText(tvTariff);
            mountFootnoteLink(DiscountId.TV_CONNECT_DISCOUNTED_PRICE_TEXT);
            mountFootnoteLink(LegalId.TEXT, LegalId.WRAPPER);
        }
    } catch (error) {
        debugInfo('Cannot fetch footnote HTML:', error);
    } finally {
        yield put(stopLoading(AppAction.LOAD_TV_FOOTNOTE));
    }
}

/**
 * App Saga
 */
export default function* saga () {
    yield takeLatest(AppAction.INIT_STORE, initStoreSaga);
    yield takeLatest(AppAction.SET_GIGAKOMBI, setGigakombiSaga);
    yield takeLatest(AppAction.SET_SELECTED_TARIFF_ID, setSelectedTariffSaga);
    yield takeLatest(AppAction.SET_YOUNG, setYoungSaga);
    yield takeLatest(AppAction.DELETE_ADDRESS, deleteAddressSaga);
    yield takeLatest(AppAction.SET_TV_PRODUCT_SELECTION, setTVSaga);
    yield takeLatest(AppAction.LOAD_TV_FOOTNOTE, setFootnoteContentSaga);
}
